<!--
  Pineapple AdminTopbar
  - DesignSystem/admin-shared.jsx 의 AdminTopbar 를 Vue 로 이식.
  - 기존 Material Navbar 가 가지고 있던 기능 보존:
    1) 글로벌 검색 (form 인풋, 실 기능은 자리표시)
    2) 환경 배지 (PROD/STAGING/DEV 자동 감지)
    3) 언어 변경 (기존 Vuex language 모듈 사용)
    4) 알림 벨 (드롭다운, 자리표시 아이템)
-->
<template>
  <div class="p-topbar">
    <!-- 좌측: 사이드바 토글 + 환영 메시지 -->
    <div class="p-topbar__left">
      <button
        class="p-topbar__menu-btn"
        :class="{ 'is-collapsed': sidebarCollapsed }"
        :title="sidebarCollapsed ? '사이드바 펼치기' : '사이드바 접기'"
        @click="toggleSidebar"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="3" y1="6"  x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      <div class="p-topbar__greet">
        <span class="p-topbar__greet-hi">{{ greeting }},</span>
        <strong class="p-topbar__greet-name">{{ userName }}</strong>
        <span class="p-topbar__greet-wave" aria-hidden="true">👋</span>
      </div>
    </div>

    <!-- 우측 -->
    <div class="p-topbar__right">
      <span class="p-topbar__env" :class="`is-${envKind}`">
        <span class="p-topbar__env-dot" />
        {{ envLabel }}
      </span>

      <!-- 언어 -->
      <div class="p-topbar__lang" v-click-outside="closeLang">
        <button class="p-topbar__icon-btn" @click="showLang = !showLang" :title="$t('navi.profile')">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        </button>
        <div v-if="showLang" class="p-topbar__menu">
          <button
            v-for="lang in languages"
            :key="lang.lan"
            class="p-topbar__menu-item"
            :class="{ 'is-active': currentLang === lang.lan }"
            @click="changeLanguage(lang.lan)"
          >
            {{ lang.title }}
          </button>
        </div>
      </div>

      <!-- 알림 -->
      <div class="p-topbar__bell" v-click-outside="closeBell">
        <button class="p-topbar__icon-btn" @click="showBell = !showBell">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          <span v-if="hasUnread" class="p-topbar__bell-dot" />
        </button>
        <div v-if="showBell" class="p-topbar__menu p-topbar__menu--wide">
          <div class="p-topbar__menu-head">알림</div>
          <div class="p-topbar__menu-empty">새로운 알림이 없습니다</div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'PAdminTopbar',
  data() {
    return {
      showLang: false,
      showBell: false,
      hasUnread: true,
      languages: [
        { title: '한국어', lan: 'korean' },
        { title: 'English', lan: 'english' },
        { title: '日本語',  lan: 'japanese' },
        { title: '中文',    lan: 'chinese' },
        { title: 'Español', lan: 'spanish' },
      ],
    };
  },
  computed: {
    sidebarCollapsed() {
      return this.$store.getters['layout/isSidebarCollapsed'];
    },
    envKind() {
      const m = this.$CONF?.NODE_ENV || process.env.VUE_APP_MODE || 'development';
      if (m === 'production') return 'prod';
      if (m === 'staging') return 'staging';
      return 'dev';
    },
    envLabel() {
      const region = 'ap-northeast-2';
      const map = { prod: 'PROD', staging: 'STAGING', dev: 'DEV' };
      return `${map[this.envKind]} · ${region}`;
    },
    currentLang() {
      return this.$store.state.language?.lan
        || this.$store.getters['language/currentLanguage']
        || this.$i18n.locale;
    },
    userName() {
      const u = this.$store.state.auth?.user_info || {};
      return u.name || u.user_name || 'Admin';
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 5) return '늦은 시간까지 수고 많으세요';
      if (h < 12) return '좋은 아침이에요';
      if (h < 18) return '오후도 힘내세요';
      return '오늘도 수고하셨어요';
    },
  },
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el.__clickOutside__ = (ev) => {
          if (!el.contains(ev.target)) binding.value();
        };
        document.addEventListener('click', el.__clickOutside__);
      },
      unmounted(el) {
        document.removeEventListener('click', el.__clickOutside__);
      },
    },
  },
  methods: {
    toggleSidebar() {
      this.$store.commit('layout/TOGGLE_SIDEBAR');
    },
    closeLang() { this.showLang = false; },
    closeBell() { this.showBell = false; },
    changeLanguage(lan) {
      this.$store.commit('language/changeLanguage', lan);
      this.showLang = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-topbar {
  height: $p-topbar-h;
  flex-shrink: 0;
  background: $p-panel;
  border-bottom: 1px solid $p-line;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  gap: 12px;
  font-family: $p-font-body;
  color: $p-ink;

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__menu-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid $p-line;
    background: $p-panel;
    color: $p-ink;
    cursor: pointer;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: $p-ink;
      color: #fff;
      border-color: $p-ink;
    }

    /* 접힘 상태에서 살짝 강조 */
    &.is-collapsed {
      background: $p-point-soft;
      border-color: $p-point-deep;
      color: $p-point-deep;
    }
  }

  &__greet {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background: $p-bg;
    border: 1px solid $p-line-soft;
    border-radius: 999px;
    font-size: 12px;
    color: $p-ink-mid;
    line-height: 1;
    flex-shrink: 0;
    min-width: 0;
    overflow: hidden;
  }
  &__greet-hi {
    color: $p-ink-mid;
    font-weight: 600;
  }
  &__greet-name {
    color: $p-ink;
    font-weight: 800;
    letter-spacing: -0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
  }
  &__greet-wave {
    font-size: 14px;
    animation: p-wave 2.4s ease-in-out infinite;
    transform-origin: 70% 70%;
  }

  @keyframes p-wave {
    0%, 60%, 100% { transform: rotate(0deg); }
    10%, 30%      { transform: rotate(14deg); }
    20%           { transform: rotate(-8deg); }
    40%, 50%      { transform: rotate(10deg); }
  }

  @media (max-width: 720px) {
    &__greet-hi, &__greet-wave { display: none; }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__env {
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    display: inline-flex;
    align-items: center;
    gap: 5px;

    &.is-prod    { background: $p-ok-soft;   color: $p-ok; }
    &.is-staging { background: $p-warn-soft; color: $p-warn; }
    &.is-dev     { background: $p-info-soft; color: $p-info; }
  }
  &__env-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }

  &__icon-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid $p-line;
    background: $p-panel;
    cursor: pointer;
    display: grid;
    place-items: center;
    color: $p-ink;
    position: relative;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover { background: $p-bg; }
  }

  &__bell { position: relative; }
  &__bell-dot {
    position: absolute;
    top: 7px;
    right: 8px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $p-err;
    border: 1.5px solid $p-panel;
  }

  &__lang { position: relative; }

  &__menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 160px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: 10px;
    box-shadow: 0 8px 24px -8px rgba(31, 27, 20, 0.18);
    padding: 6px;
    z-index: 1000;

    &--wide { min-width: 280px; }
  }
  &__menu-head {
    padding: 8px 10px 6px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $p-ink-soft;
  }
  &__menu-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 10px;
    border: 0;
    background: transparent;
    border-radius: 6px;
    font-family: $p-font-body;
    font-size: 13px;
    color: $p-ink;
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover { background: $p-bg; }
    &.is-active {
      background: $p-point-soft;
      color: $p-point-deep;
      font-weight: 700;
    }
  }
  &__menu-empty {
    padding: 16px 10px;
    font-size: 12px;
    color: $p-ink-soft;
    text-align: center;
  }

}
</style>
